import { supabase } from '../lib/supabase';

const participantesService = {
  // Buscar participantes de um teste específico
  async getByTesteId(testeId) {
    try {
      const { data, error } = await supabase
        .from('teste_participantes')
        .select(`
          *,
          alunos (
            id,
            nome,
            email,
            ra,
            escola_id,
            ano_escolar_id,
            periodo,
            escolas (
              id,
              nome
            ),
            anos_escolares (
              id,
              nome
            )
          )
        `)
        .eq('teste_id', testeId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar participantes:', error);
      throw error;
    }
  },

  // Buscar alunos disponíveis para adicionar (não participantes ainda)
  async getAlunosDisponiveis(testeId, filters = {}) {
    try {
      let query = supabase
        .from('alunos')
        .select(`
          id,
          nome,
          email,
          ra,
          escola_id,
          ano_escolar_id,
          periodo,
          ativo,
          escolas (
            id,
            nome
          ),
          anos_escolares (
            id,
            nome
          )
        `);

      // Aplicar filtros
      if (filters.escola_id) {
        query = query.eq('escola_id', filters.escola_id);
      }

      if (filters.ano_escolar_id) {
        query = query.eq('ano_escolar_id', filters.ano_escolar_id);
      }

      if (filters.search) {
        query = query.or(`nome.ilike.%${filters.search}%,email.ilike.%${filters.search}%,ra.ilike.%${filters.search}%`);
      }

      const { data: alunos, error } = await query.order('nome');

      if (error) throw error;

      // Buscar participantes já adicionados
      const { data: participantes, error: participantesError } = await supabase
        .from('teste_participantes')
        .select('aluno_id')
        .eq('teste_id', testeId);

      if (participantesError) throw participantesError;

      // Filtrar alunos que já são participantes
      const participantesIds = participantes.map(p => p.aluno_id);
      const alunosDisponiveis = alunos.filter(aluno => !participantesIds.includes(aluno.id));

      return alunosDisponiveis;
    } catch (error) {
      console.error('Erro ao buscar alunos disponíveis:', error);
      throw error;
    }
  },

  // Adicionar participante
  async create(testeId, alunoId, observacoes = '') {
    try {
      const { data, error } = await supabase
        .from('teste_participantes')
        .insert({
          teste_id: testeId,
          aluno_id: alunoId,
          observacoes,
          ativo: true
        })
        .select(`
          *,
          alunos (
            id,
            nome,
            email,
            ra,
            escolas (
              id,
              nome
            ),
            anos_escolares (
              id,
              nome
            )
          )
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao adicionar participante:', error);
      throw error;
    }
  },

  // Adicionar múltiplos participantes
  async createMultiple(testeId, alunoIds, observacoes = '') {
    try {
      const participantes = alunoIds.map(alunoId => ({
        teste_id: testeId,
        aluno_id: alunoId,
        observacoes,
        ativo: true
      }));

      const { data, error } = await supabase
        .from('teste_participantes')
        .insert(participantes)
        .select(`
          *,
          alunos (
            id,
            nome,
            email,
            ra,
            escolas (
              id,
              nome
            ),
            anos_escolares (
              id,
              nome
            )
          )
        `);

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao adicionar participantes:', error);
      throw error;
    }
  },

  // Remover participante
  async delete(id) {
    try {
      const { error } = await supabase
        .from('teste_participantes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Erro ao remover participante:', error);
      throw error;
    }
  },

  // Remover múltiplos participantes
  async deleteMultiple(ids) {
    try {
      const { error } = await supabase
        .from('teste_participantes')
        .delete()
        .in('id', ids);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Erro ao remover participantes:', error);
      throw error;
    }
  },

  // Ativar/desativar participante
  async toggleActive(id, ativo) {
    try {
      const { data, error } = await supabase
        .from('teste_participantes')
        .update({ ativo })
        .eq('id', id)
        .select(`
          *,
          alunos (
            id,
            nome,
            email,
            ra,
            escolas (
              id,
              nome
            )
          )
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao alterar status do participante:', error);
      throw error;
    }
  },

  // Contar participantes de um teste
  async countByTesteId(testeId) {
    try {
      const { count, error } = await supabase
        .from('teste_participantes')
        .select('*', { count: 'exact', head: true })
        .eq('teste_id', testeId);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Erro ao contar participantes:', error);
      return 0;
    }
  },

  // Buscar escolas para filtro
  async getEscolas() {
    try {
      const { data, error } = await supabase
        .from('escolas')
        .select('id, nome')
        .order('nome');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar escolas:', error);
      throw error;
    }
  },

  // Buscar anos escolares para filtro
  async getAnosEscolares() {
    try {
      const { data, error } = await supabase
        .from('anos_escolares')
        .select('id, nome')
        .order('nome');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar anos escolares:', error);
      throw error;
    }
  }
};

export default participantesService;
